import React, { useCallback, useEffect, useMemo, useState, createElement } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { Banknote, CreditCard, Smartphone, Download, Calendar, RefreshCw, Receipt, Utensils, Bike, ShoppingBag } from 'lucide-react';
// ──────────────────────────────────────────────────────────────────
// CashClose — La Cabaña Parrillera ONLY
// Renders a per-product sales log for a chosen date, with timestamp,
// price, payment method and channel (mesa / delivery / pickup).
// Reads from the `payments` table (items_json snapshot) joined with
// `orders` for delivery_type. Read-only — does NOT mutate any data.
// ──────────────────────────────────────────────────────────────────
interface CabanaCashCloseProps {
  restaurantId: number | string;
}
interface PaymentRow {
  id: string;
  restaurant_id: number;
  order_id: string;
  table_id: string | null;
  amount: number;
  method: string;
  items_json: any[];
  created_at: string;
  is_partial: boolean;
}
interface OrderMeta {
  id: string;
  delivery_type: string | null;
  table_id: number | null;
  order_number: string | null;
}
interface FlatRow {
  payment_id: string;
  order_id: string;
  order_number: string | null;
  created_at: string;
  method: string;
  channel: 'mesa' | 'delivery' | 'pickup' | 'desconocido';
  table_id: number | null;
  /** Human-readable table label (e.g. "Mesa 7" or "Barra 2"). Null if not a mesa order. */
  table_label: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  notes: string | null;
}
const todayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const methodMeta = (m: string) => {
  switch (m) {
    case 'cash':
      return {
        label: 'Efectivo',
        Icon: Banknote,
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
      };
    case 'sinpe':
      return {
        label: 'SINPE',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'card':
    case 'tarjeta':
      return {
        label: 'Tarjeta',
        Icon: CreditCard,
        chip: 'bg-violet-50 text-violet-700 border-violet-200'
      };
    case 'transfer':
    case 'transferencia':
      return {
        label: 'Transferencia',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'unknown':
    case '':
    case 'sin_metodo':
      return {
        label: 'Sin método',
        Icon: Receipt,
        chip: 'bg-gray-100 text-gray-700 border-gray-300'
      };
    default:
      return {
        label: m,
        Icon: Banknote,
        chip: 'bg-gray-50 text-gray-700 border-gray-200'
      };
  }
};
const channelMeta = (c: FlatRow['channel'], tableLabel: string | null) => {
  switch (c) {
    case 'mesa':
      return {
        label: tableLabel || 'Mesa',
        Icon: Utensils,
        chip: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    case 'delivery':
      return {
        label: 'Delivery',
        Icon: Bike,
        chip: 'bg-cyan-50 text-cyan-700 border-cyan-200'
      };
    case 'pickup':
      return {
        label: 'Para llevar',
        Icon: ShoppingBag,
        chip: 'bg-orange-50 text-orange-700 border-orange-200'
      };
    default:
      return {
        label: '—',
        Icon: Receipt,
        chip: 'bg-gray-50 text-gray-600 border-gray-200'
      };
  }
};
const fmtTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
const fmtFull = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  } catch {
    return iso;
  }
};
export function CabanaCashClose({
  restaurantId
}: CabanaCashCloseProps) {
  const [date, setDate] = useState<string>(todayIso());
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [orderMeta, setOrderMeta] = useState<Record<string, OrderMeta>>({});
  /** Maps restaurant_tables.id → human label (e.g. "Mesa 7" / "Barra 2"). */
  const [tableLabels, setTableLabels] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    try {
      // Build day window in local time
      const start = new Date(`${date}T00:00:00`);
      const end = new Date(`${date}T23:59:59.999`);
      // ─── (1) Load payments rows for the day ─────────────────────────
      // These are the "official" cash close rows generated by the
      // Cobrar / Facturar flow (processPayment).
      const {
        data: pays,
        error: payErr
      } = await supabase.from('payments').select('id, restaurant_id, order_id, table_id, amount, method, items_json, created_at, is_partial').eq('restaurant_id', Number(restaurantId)).gte('created_at', start.toISOString()).lte('created_at', end.toISOString()).order('created_at', {
        ascending: true
      });
      if (payErr) throw payErr;
      const safePays = (pays || []) as PaymentRow[];
      // ─── (2) Load ALL closed/paid orders for the day ───────────────
      // Captures orders closed by ANY path:
      //   • "Cobrar / Facturar" → status=delivered + payment_method set
      //   • "Entregar al cliente" (Caja) → status=delivered
      //   • Dashboard / marketplace fulfillment → status=delivered|completed
      //   • Any order where a method was registered → payment_method != null
      //
      // We filter by `created_at` (immutable) instead of `updated_at`
      // (mutates on every touch) so historical days are stable and
      // reproducible. Dine-in orders for La Cabaña are created and closed
      // the same day, which makes this the natural mental model.
      const {
        data: deliveredOrdersRaw,
        error: delErr
      } = await supabase.from('orders').select('id, order_number, delivery_type, table_id, payment_method, total, status, delivered_at, updated_at, created_at').eq('restaurant_id', Number(restaurantId)).gte('created_at', start.toISOString()).lte('created_at', end.toISOString()).or('status.eq.delivered,status.eq.completed,status.eq.paid,payment_method.not.is.null');
      if (delErr) throw delErr;
      // Statuses that DISQUALIFY an order from the cash close, no matter
      // what payment_method is set on the row. A cancelled order may still
      // carry a payment_method from before it was cancelled — if we don't
      // exclude it explicitly, its total leaks into the day's revenue.
      const EXCLUDED_STATUSES = new Set(['cancelled', 'canceled', 'cancelado', 'refunded', 'reembolsado', 'voided', 'rejected']);
      // Final safety filter in JS: keep only orders that look "closed".
      // Cancelled / refunded / voided orders are ALWAYS rejected, even if
      // they still have a payment_method on them.
      const deliveredOrders = (deliveredOrdersRaw || []).filter((o: any) => {
        const s = String(o.status || '').toLowerCase().trim();
        if (EXCLUDED_STATUSES.has(s)) return false;
        const closedStatuses = new Set(['delivered', 'completed', 'paid', 'out_for_delivery']);
        return closedStatuses.has(s) || o.payment_method && String(o.payment_method).trim() !== '';
      });
      // ─── (3) Build the union of orders we need metadata for ─────────
      const paymentOrderIds = Array.from(new Set(safePays.map(p => String(p.order_id)))).filter(Boolean);
      const deliveredOrderIds = Array.from(new Set((deliveredOrders || []).map((o: any) => String(o.id))));
      // Extra safety net: catch order_items that were marked paid_at within
      // the day window. Useful if their parent order was created on a
      // different day but cobrado within the selected day.
      let extraPaidOrderIds: string[] = [];
      try {
        const {
          data: paidItems
        } = await supabase.from('order_items').select('order_id').gte('paid_at', start.toISOString()).lte('paid_at', end.toISOString());
        extraPaidOrderIds = Array.from(new Set((paidItems || []).map((it: any) => String(it.order_id)).filter(Boolean)));
      } catch {
        // Non-fatal: keep going with the primary date window
        extraPaidOrderIds = [];
      }
      const allOrderIds = Array.from(new Set([...paymentOrderIds, ...deliveredOrderIds, ...extraPaidOrderIds]));
      // ─── (4) Fetch order metadata for any payment-only orders OR
      // orders surfaced by the paid-items safety net that weren't already
      // in the delivered set. ────────────────────────────────────────────
      const missingMetaIds = allOrderIds.filter(id => !deliveredOrderIds.includes(id));
      let extraOrders: any[] = [];
      if (missingMetaIds.length > 0) {
        const {
          data: extra,
          error: exErr
        } = await supabase.from('orders').select('id, order_number, delivery_type, table_id, payment_method, total, status, delivered_at, updated_at, created_at').in('id', missingMetaIds);
        if (exErr) throw exErr;
        extraOrders = extra || [];
      }
      const allOrders = [...(deliveredOrders || []), ...extraOrders];
      // Build a set of orderIds that should NEVER appear in the cash close
      // because their underlying order was cancelled / refunded / voided.
      // We pull from extraOrders too — that's where real `payments` rows
      // pointing at cancelled orders surface, since cancelled orders are
      // filtered out of `deliveredOrders` above.
      const cancelledOrderIds = new Set<string>(allOrders.filter((o: any) => EXCLUDED_STATUSES.has(String(o.status || '').toLowerCase().trim())).map((o: any) => String(o.id)));
      // Drop any real `payments` row whose order is cancelled — even
      // though the payment row itself still exists, its revenue MUST NOT
      // count toward the day's totals.
      const filteredSafePays = safePays.filter(p => !cancelledOrderIds.has(String(p.order_id)));
      // ─── (5) Build orderMeta map ────────────────────────────────────
      const metaMap: Record<string, OrderMeta> = {};
      allOrders.forEach((o: any) => {
        // Skip cancelled orders so they don't even render as "metadata"
        // (avoids any stale UI references to a cancelled order's table).
        if (cancelledOrderIds.has(String(o.id))) return;
        metaMap[String(o.id)] = {
          id: String(o.id),
          delivery_type: o.delivery_type,
          table_id: o.table_id != null ? Number(o.table_id) : null,
          order_number: o.order_number || null
        };
      });
      setOrderMeta(metaMap);
      // ─── (6) Synthesize payment rows for closed orders that did
      // NOT go through the Cobrar / Facturar flow (e.g. "Entregar al
      // cliente" from Caja, marketplace fulfillment, etc.) ──────────────
      const paidOrderIdSet = new Set(filteredSafePays.map(p => String(p.order_id)));
      // Union: delivered+closed orders for the day, plus anything caught
      // by the paid-items safety net. Cancelled orders are excluded.
      const synthCandidates = [...(deliveredOrders || []), ...extraOrders.filter((o: any) => !deliveredOrderIds.includes(String(o.id)) && extraPaidOrderIds.includes(String(o.id)) && !cancelledOrderIds.has(String(o.id)))];
      const unpaidDelivered = synthCandidates.filter((o: any) => !paidOrderIdSet.has(String(o.id)) && !cancelledOrderIds.has(String(o.id)));
      let syntheticPays: PaymentRow[] = [];
      if (unpaidDelivered.length > 0) {
        // Fetch their items so we can build per-product rows
        const unpaidIds = unpaidDelivered.map((o: any) => o.id);
        const {
          data: items,
          error: itErr
        } = await supabase.from('order_items').select('order_id, item_name_snapshot, quantity, unit_price_snapshot, line_total, notes').in('order_id', unpaidIds);
        if (itErr) {
          // Non-fatal: still show a single-line synthetic row using total
          console.warn('[CabanaCashClose] could not load order_items for synthetic rows', itErr);
        }
        const itemsByOrder: Record<string, any[]> = {};
        (items || []).forEach((it: any) => {
          const k = String(it.order_id);
          if (!itemsByOrder[k]) itemsByOrder[k] = [];
          itemsByOrder[k].push(it);
        });
        syntheticPays = unpaidDelivered.map((o: any) => {
          const oid = String(o.id);
          const oItems = itemsByOrder[oid] || [];
          const itemsJson = oItems.length > 0 ? oItems.map((it: any) => ({
            item_name: it.item_name_snapshot || '—',
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price_snapshot) || 0,
            line_total: Number(it.line_total) || 0,
            notes: it.notes || null
          })) : [];
          return {
            id: `synthetic-${oid}`,
            restaurant_id: Number(restaurantId),
            order_id: oid,
            table_id: o.table_id != null ? String(o.table_id) : null,
            amount: Number(o.total) || 0,
            // If the order never registered a method, mark it as unknown
            method: o.payment_method || 'unknown',
            items_json: itemsJson,
            // Best "closed at" timestamp available
            created_at: o.delivered_at || o.updated_at || o.created_at,
            is_partial: false
          } as PaymentRow;
        });
      }
      // ─── (7) Merge real payments + synthetic, sort by time ──────────
      // Use the cancellation-filtered list of real payments so cancelled
      // orders never contribute to the day's totals.
      const merged = [...filteredSafePays, ...syntheticPays].sort((a, b) => a.created_at.localeCompare(b.created_at));
      setPayments(merged);
      // ─── (8) Resolve table_id (FK) → label for every table referenced ─
      const tableIds = Array.from(new Set(allOrders.map((o: any) => o.table_id).filter((v: any) => v != null).map((v: any) => Number(v)).concat(safePays.map(p => p.table_id != null ? Number(p.table_id) : null).filter((v): v is number => v != null))));
      if (tableIds.length > 0) {
        const {
          data: tablesData,
          error: tblErr
        } = await supabase.from('restaurant_tables').select('id, table_number, table_name').in('id', tableIds);
        if (tblErr) {
          console.warn('[CabanaCashClose] could not resolve table labels', tblErr);
          setTableLabels({});
        } else {
          const tMap: Record<number, string> = {};
          (tablesData || []).forEach((t: any) => {
            const label = t.table_name && String(t.table_name).trim() || (t.table_number != null ? `Mesa ${t.table_number}` : `Mesa ${t.id}`);
            tMap[Number(t.id)] = label;
          });
          setTableLabels(tMap);
        }
      } else {
        setTableLabels({});
      }
    } catch (err: any) {
      console.error('[CabanaCashClose] load error', err);
      setError(err?.message || 'No se pudieron cargar los pagos.');
      setPayments([]);
      setOrderMeta({});
      setTableLabels({});
    } finally {
      setLoading(false);
    }
  }, [restaurantId, date]);
  useEffect(() => {
    load();
  }, [load]);
  // ─── Auto-refresh: only when the displayed date is "today" ───
  // Refreshes every 20s so newly processed payments appear without
  // forcing the user to click ⟳. Past dates never auto-refresh.
  useEffect(() => {
    if (date !== todayIso()) return;
    const id = setInterval(() => {
      // Avoid stacking requests if a load is already in flight
      if (!loading) load();
    }, 20000);
    return () => clearInterval(id);
  }, [date, loading, load]);
  // Flatten payments → one row per item sold
  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    for (const p of payments) {
      const meta = orderMeta[p.order_id];
      let channel: FlatRow['channel'] = 'desconocido';
      if (meta?.delivery_type === 'delivery') channel = 'delivery';else if (meta?.delivery_type === 'pickup') channel = 'pickup';else if (meta?.delivery_type === 'dine_in' || meta?.table_id != null) channel = 'mesa';else if (p.table_id != null) channel = 'mesa';
      // Resolve the human-readable mesa label using either the order's
      // table_id or the payment's table_id (whichever exists). table_id
      // here is the DB PK (e.g. 44); the label maps it to "Mesa 7".
      const rawTableId = meta?.table_id ?? (p.table_id != null ? Number(p.table_id) : null);
      const resolvedLabel = rawTableId != null ? tableLabels[Number(rawTableId)] || null : null;
      const items = Array.isArray(p.items_json) ? p.items_json : [];
      if (items.length === 0) {
        rows.push({
          payment_id: p.id,
          order_id: p.order_id,
          order_number: meta?.order_number || null,
          created_at: p.created_at,
          method: p.method,
          channel,
          table_id: rawTableId,
          table_label: resolvedLabel,
          item_name: '(pago sin desglose)',
          quantity: 1,
          unit_price: p.amount,
          line_total: p.amount,
          notes: null
        });
      } else {
        for (const it of items) {
          rows.push({
            payment_id: p.id,
            order_id: p.order_id,
            order_number: meta?.order_number || null,
            created_at: p.created_at,
            method: p.method,
            channel,
            table_id: rawTableId,
            table_label: resolvedLabel,
            item_name: it.item_name || '—',
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
            line_total: Number(it.line_total) || 0,
            notes: it.notes || null
          });
        }
      }
    }
    return rows;
  }, [payments, orderMeta, tableLabels]);
  // Totals
  const totals = useMemo(() => {
    const byMethod: Record<string, {
      total: number;
      count: number;
    }> = {
      cash: {
        total: 0,
        count: 0
      },
      sinpe: {
        total: 0,
        count: 0
      },
      card: {
        total: 0,
        count: 0
      }
    };
    const byChannel: Record<string, {
      total: number;
      count: number;
    }> = {
      mesa: {
        total: 0,
        count: 0
      },
      delivery: {
        total: 0,
        count: 0
      },
      pickup: {
        total: 0,
        count: 0
      },
      desconocido: {
        total: 0,
        count: 0
      }
    };
    let grand = 0;
    let itemCount = 0;
    for (const r of flatRows) {
      grand += r.line_total;
      itemCount += r.quantity;
      if (!byMethod[r.method]) byMethod[r.method] = {
        total: 0,
        count: 0
      };
      byMethod[r.method].total += r.line_total;
      byMethod[r.method].count += r.quantity;
      byChannel[r.channel].total += r.line_total;
      byChannel[r.channel].count += r.quantity;
    }
    return {
      byMethod,
      byChannel,
      grand,
      itemCount,
      paymentCount: payments.length
    };
  }, [flatRows, payments.length]);
  const handleExportCsv = () => {
    const headers = ['Fecha y hora', 'Pedido', 'Producto', 'Cantidad', 'Precio unitario', 'Total línea', 'Método de pago', 'Canal', 'Notas'];
    const escape = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    for (const r of flatRows) {
      lines.push([escape(fmtFull(r.created_at)), escape(r.order_number || r.order_id.slice(0, 8)), escape(r.item_name), escape(r.quantity), escape(r.unit_price), escape(r.line_total), escape(methodMeta(r.method).label), escape(channelMeta(r.channel, r.table_label).label), escape(r.notes || '')].join(','));
    }
    // Append totals
    lines.push('');
    lines.push(`TOTAL,,,,,${totals.grand},,,`);
    const csv = lines.join('\n');
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cierre-caja-la-cabana_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <Card className="border-amber-200" data-id="element-314">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-315">
        <div className="flex items-center gap-3" data-id="element-316">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center" data-id="element-317">
            <Receipt className="w-5 h-5 text-amber-600" data-id="element-318" />
          </div>
          <div data-id="element-319">
            <CardTitle className="text-lg" data-id="element-320">Cierre de Caja</CardTitle>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-321">
              Ventas detalladas del día con producto, precio, método de pago y
              canal.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-322">
          <div className="relative" data-id="element-323">
            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-324" />
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="pl-9 pr-3 h-9 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:border-amber-400" data-id="element-325" />
          </div>
          <button onClick={() => setDate(todayIso())} className="h-9 px-3 rounded-lg text-xs font-bold bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors" data-id="element-326">
            Hoy
          </button>
          <button onClick={() => load()} disabled={loading} className="h-9 w-9 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50 relative" title={date === todayIso() ? 'Refrescar (auto cada 20s)' : 'Refrescar'} data-id="element-327">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-328" />
            {date === todayIso() && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-329" />}
          </button>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={loading || flatRows.length === 0} data-id="element-330">
            <Download className="w-4 h-4 mr-2" data-id="element-331" />
            CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5" data-id="element-332">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-333">
          <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-amber-50 to-white p-3" data-id="element-334">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider" data-id="element-335">
              Total del día
            </p>
            <p className="text-2xl font-black text-gray-900 tabular-nums mt-1" data-id="element-336">
              {formatCurrency(totals.grand)}
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-337">
              {totals.paymentCount} cobro
              {totals.paymentCount === 1 ? '' : 's'} · {totals.itemCount} ítem
              {totals.itemCount === 1 ? '' : 's'}
            </p>
          </div>
          {(['cash', 'sinpe', 'card'] as const).map(m => {
          const meta = methodMeta(m);
          const t = totals.byMethod[m] || {
            total: 0,
            count: 0
          };
          return <div key={m} className="rounded-xl border border-gray-100 bg-white p-3" data-id="element-338">
                <div className="flex items-center gap-1.5" data-id="element-339">
                  <meta.Icon className="w-3.5 h-3.5 text-gray-500" data-id="element-340" />
                  <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider" data-id="element-341">
                    {meta.label}
                  </p>
                </div>
                <p className="text-xl font-black text-gray-900 tabular-nums mt-1" data-id="element-342">
                  {formatCurrency(t.total)}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-343">
                  {t.count} ítem{t.count === 1 ? '' : 's'}
                </p>
              </div>;
        })}
        </div>

        {/* Channel breakdown */}
        <div className="flex flex-wrap gap-2" data-id="element-344">
          {(['mesa', 'delivery', 'pickup'] as const).map(c => {
          const meta = channelMeta(c, null);
          const t = totals.byChannel[c] || {
            total: 0,
            count: 0
          };
          return <div key={c} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold ${meta.chip}`} data-id="element-345">
                <meta.Icon className="w-3.5 h-3.5" data-id="element-346" />
                <span data-id="element-347">{meta.label}</span>
                <span className="tabular-nums" data-id="element-348">{formatCurrency(t.total)}</span>
                <span className="opacity-60" data-id="element-349">· {t.count}</span>
              </div>;
        })}
        </div>

        {/* Table */}
        {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-350">
            {error}
          </div> : loading ? <div className="space-y-2" data-id="element-351">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" data-id="element-352" />)}
          </div> : flatRows.length === 0 ? <div className="text-center py-10 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl" data-id="element-353">
            <p className="font-medium text-gray-700" data-id="element-354">
              No hay ventas cerradas registradas para{' '}
              {date === todayIso() ? 'hoy' : 'esta fecha'}.
            </p>
            <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto leading-relaxed" data-id="element-355">
              Aparecen automáticamente las órdenes cobradas o entregadas desde
              Caja, Dashboard, Mesas y cualquier otro flujo. Si no ves datos de
              un día anterior, verifica que esas órdenes tengan estado entregado
              o método de pago registrado.
            </p>
            {date === todayIso() && <p className="text-[11px] text-emerald-700 mt-3 inline-flex items-center gap-1.5" data-id="element-356">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-357" />
                Actualización automática activa
              </p>}
          </div> : <div className="overflow-x-auto -mx-6" data-id="element-358">
            <table className="w-full text-sm text-left min-w-[760px]" data-id="element-359">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-360">
                <tr data-id="element-361">
                  <th className="px-6 py-2.5 rounded-l-lg" data-id="element-362">Hora</th>
                  <th className="px-3 py-2.5" data-id="element-363">Pedido</th>
                  <th className="px-3 py-2.5" data-id="element-364">Producto</th>
                  <th className="px-3 py-2.5 text-center" data-id="element-365">Cant.</th>
                  <th className="px-3 py-2.5 text-right" data-id="element-366">Precio</th>
                  <th className="px-3 py-2.5" data-id="element-367">Método</th>
                  <th className="px-3 py-2.5 rounded-r-lg" data-id="element-368">Canal</th>
                </tr>
              </thead>
              <tbody data-id="element-369">
                {flatRows.map((r, idx) => {
              const mMeta = methodMeta(r.method);
              const cMeta = channelMeta(r.channel, r.table_label);
              return <tr key={`${r.payment_id}-${idx}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-370">
                      <td className="px-6 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-371">
                        {fmtTime(r.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs tabular-nums whitespace-nowrap" data-id="element-372">
                        #
                        {r.order_number ? r.order_number.split('-').pop() : r.order_id.slice(0, 6)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-373">
                        <div className="font-medium text-gray-900" data-id="element-374">
                          {r.item_name}
                        </div>
                        {r.notes && <div className="text-[11px] text-gray-500 italic" data-id="element-375">
                            {r.notes}
                          </div>}
                      </td>
                      <td className="px-3 py-2.5 text-center text-gray-700 tabular-nums" data-id="element-376">
                        {r.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap" data-id="element-377">
                        {formatCurrency(r.line_total)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-378">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${mMeta.chip}`} data-id="element-379">
                          <mMeta.Icon className="w-3 h-3" data-id="element-380" />
                          {mMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5" data-id="element-381">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${cMeta.chip}`} data-id="element-382">
                          <cMeta.Icon className="w-3 h-3" data-id="element-383" />
                          {cMeta.label}
                        </span>
                      </td>
                    </tr>;
            })}
              </tbody>
              <tfoot data-id="element-384">
                <tr className="border-t-2 border-gray-200 bg-amber-50/50" data-id="element-385">
                  <td colSpan={4} className="px-6 py-3 font-black text-gray-900" data-id="element-386">
                    TOTAL DEL DÍA
                  </td>
                  <td className="px-3 py-3 text-right font-black text-amber-600 text-base tabular-nums" data-id="element-387">
                    {formatCurrency(totals.grand)}
                  </td>
                  <td colSpan={2} className="px-3 py-3 text-xs text-gray-500" data-id="element-388">
                    {totals.paymentCount} cobros · {totals.itemCount} ítems
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>}
      </CardContent>
    </Card>;
}